import React, { useState } from 'react';
import { Utensils, Users, Calendar, Heart, Cake, Star, Flame, Leaf, Award, ChefHat } from 'lucide-react';
import { mockData, mockFormHandlers } from '../mock';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../components/ui/card';
import { PaymentSelection } from '../components/PaymentSelection';
import { useToast } from '../hooks/use-toast';

export const Services = () => {
  const [popupEmail, setPopupEmail] = useState('');
  const [selectedTier, setSelectedTier] = useState(null);
  const [showPaymentSelection, setShowPaymentSelection] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedMenuCategory, setSelectedMenuCategory] = useState('appetizers');
  const { toast } = useToast();

  // Helper functions for Indian Fusion Menu
  const getSpiceIcon = (level) => {
    const flames = {
      'None': 0,
      'Mild': 1,
      'Medium': 2,
      'Medium-Hot': 3,
      'Hot': 4
    };
    
    return Array.from({ length: 4 }, (_, i) => (
      <Flame 
        key={i} 
        size={12} 
        style={{ 
          color: i < flames[level] ? '#ef4444' : '#e5e7eb',
          marginRight: '2px'
        }} 
      />
    ));
  };

  const getDietaryIcon = (info) => {
    if (info.includes('Vegetarian') || info.includes('Vegan')) return <Leaf size={14} color="#10b981" />;
    if (info.includes('Premium')) return <Award size={14} color="#d4af37" />;
    return null;
  };

  const menuCategories = [
    { key: 'appetizers', label: 'Appetizers', icon: <ChefHat size={20} /> },
    { key: 'mains', label: 'Main Courses', icon: <Star size={20} /> },
    { key: 'desserts', label: 'Desserts', icon: <Award size={20} /> }
  ];

  const handlePopupSignup = async (e) => {
    e.preventDefault();
    if (!popupEmail) return;

    setIsSubmitting(true);
    try {
      const result = await mockFormHandlers.joinPopupEvents(popupEmail);
      if (result.success) {
        toast({
          title: "Success!",
          description: result.message,
        });
        setPopupEmail('');
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Something went wrong. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleTierSubscription = async (tier) => {
    setSelectedTier(tier);
    setShowPaymentSelection(true);
  };

  const handlePaymentCompleted = (paymentData) => {
    toast({
      title: "Subscription Activated!",
      description: `Welcome to ${selectedTier.name}! Your benefits are now active.`,
    });
    setShowPaymentSelection(false);
    setSelectedTier(null);
  };

  const serviceIcons = {
    0: Utensils,
    1: Heart,
    2: Users,
    3: Calendar,
    4: Star
  };

  return (
    <div>
      {/* Services Hero */}
      <section className="section-padding">
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: '80px' }}>
            <h1 className="hero-large" style={{ marginBottom: '24px' }}>Luxury Hospitality Experiences</h1>
            <p className="body-large" style={{ color: 'var(--text-secondary)', maxWidth: '700px', margin: '0 auto', lineHeight: '1.6' }}>
              We provide high-end luxury hospitality experiences from large to small events, weddings, travel groups, retreats, and elite celebrations reaching unparalleled sophistication. 
              We don't just cook — we provide unforgettable moments and services.
            </p>
          </div>
        </div>
      </section>

      {/* Main Services */}
      <section style={{ background: 'var(--bg-secondary)', padding: '80px 0' }}>
        <div className="container">
          <h2 className="heading-1" style={{ textAlign: 'center', marginBottom: '80px' }}>
            Comprehensive Luxury Hospitality Services
          </h2>
          
          <div className="grid-three-column">
            {mockData.services.map((service, index) => {
              const IconComponent = serviceIcons[index] || Utensils;
              return (
                <Card key={service.id} className="hover-lift" style={{
                  border: '1px solid var(--border-light)',
                  borderRadius: '0px',
                  background: 'var(--bg-primary)',
                  padding: '32px 24px',
                  textAlign: 'center'
                }}>
                  <CardHeader>
                    <div style={{
                      width: '80px',
                      height: '80px',
                      background: index % 2 === 0 ? 'var(--color-fence-green)' : 'var(--color-gold)',
                      borderRadius: '0px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      margin: '0 auto 24px auto'
                    }}>
                      <IconComponent size={32} color="white" />
                    </div>
                    <CardTitle className="heading-3">{service.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="body-regular" style={{ marginBottom: '24px' }}>
                      {service.description}
                    </CardDescription>
                    <ul style={{ textAlign: 'left', listStyle: 'none', padding: 0 }}>
                      {service.features.map((feature, idx) => (
                        <li key={idx} className="body-small" style={{ 
                          marginBottom: '8px',
                          color: 'var(--text-secondary)',
                          paddingLeft: '16px',
                          position: 'relative'
                        }}>
                          <span style={{
                            position: 'absolute',
                            left: 0,
                            top: '50%',
                            transform: 'translateY(-50%)',
                            width: '6px',
                            height: '6px',
                            background: 'var(--color-gold)',
                            borderRadius: '50%'
                          }}></span>
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Pop-up Events Signup */}
      <section className="section-padding">
        <div className="container">
          <div style={{ 
            background: 'var(--color-lavender)', 
            padding: '80px 40px',
            textAlign: 'center',
            borderRadius: '0px'
          }}>
            <Calendar size={48} color="var(--color-fence-green)" style={{ margin: '0 auto 24px auto' }} />
            <h2 className="heading-1" style={{ marginBottom: '24px' }}>Join Our Pop-Up Events</h2>
            <p className="body-large" style={{ 
              marginBottom: '40px', 
              color: 'var(--text-secondary)',
              maxWidth: '600px',
              margin: '0 auto 40px auto'
            }}>
              Be the first to know about our exclusive pop-up dining events featuring seasonal menus, 
              special collaborations, and unique culinary experiences.
            </p>
            
            <form onSubmit={handlePopupSignup} style={{ 
              display: 'flex', 
              gap: '16px', 
              maxWidth: '500px', 
              margin: '0 auto',
              flexWrap: 'wrap',
              justifyContent: 'center'
            }}>
              <Input
                type="email"
                placeholder="Enter your email for event notifications"
                value={popupEmail}
                onChange={(e) => setPopupEmail(e.target.value)}
                required
                style={{ 
                  borderRadius: '0px',
                  border: '1px solid var(--border-medium)',
                  minWidth: '300px',
                  flex: '1'
                }}
              />
              <Button 
                type="submit" 
                className="btn-primary"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Signing Up...' : 'Join Events'}
              </Button>
            </form>
          </div>
        </div>
      </section>

      {/* Premium Service Offerings */}
      <section className="section-padding">
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: '80px' }}>
            <h2 className="heading-1" style={{ marginBottom: '24px' }}>Premium Service Offerings</h2>
            <p className="body-large" style={{ 
              color: 'var(--text-secondary)', 
              maxWidth: '700px', 
              margin: '0 auto' 
            }}>
              Discover our expanded collection of specialized culinary services, each designed to create 
              unforgettable moments with artisanal presentation and exceptional attention to detail.
            </p>
          </div>
          
          <div className="grid-two-column" style={{ alignItems: 'flex-start', gap: '60px' }}>
            {/* Services Menu Image */}
            <div style={{ position: 'relative' }}>
              <img
                src={mockData.heroImages[0].url}
                alt="Decadent Palete Premium Culinary Experience"
                style={{
                  width: '100%',
                  height: 'auto',
                  borderRadius: '0px',
                  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)'
                }}
              />
            </div>
            
            {/* Services Description */}
            <div>
              <h3 className="heading-2" style={{ marginBottom: '32px' }}>
                Specialized Culinary Experiences
              </h3>
              
              <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                {mockData.premiumServices.services.map((service, index) => (
                  <div key={index} style={{
                    padding: '20px',
                    border: '1px solid var(--border-light)',
                    borderRadius: '0px',
                    background: index % 2 === 0 ? 'var(--bg-primary)' : 'var(--color-lavender)',
                    transition: 'all 0.2s ease'
                  }}
                  className="hover-lift">
                    <h4 className="heading-3" style={{ 
                      marginBottom: '8px',
                      color: index % 2 === 0 ? 'var(--text-primary)' : 'var(--color-fence-green)'
                    }}>
                      {service.name}
                    </h4>
                    <p className="body-regular" style={{ 
                      color: 'var(--text-secondary)',
                      margin: 0
                    }}>
                      {service.description}
                    </p>
                  </div>
                ))}
              </div>
              
              <div style={{ 
                marginTop: '40px',
                padding: '24px',
                background: 'var(--color-fence-green)',
                color: 'white',
                textAlign: 'center'
              }}>
                <h4 className="heading-3" style={{ marginBottom: '16px', color: 'white' }}>
                  Custom Service Packages Available
                </h4>
                <p className="body-regular" style={{ 
                  marginBottom: '24px',
                  color: 'rgba(255, 255, 255, 0.9)'
                }}>
                  All services can be customized to your specific needs, dietary requirements, 
                  and event size. Contact us for detailed pricing and availability.
                </p>
                <Button className="btn-gold">
                  Request Custom Quote
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Dessert Services */}
      <section style={{ background: 'var(--bg-secondary)', padding: '80px 0' }}>
        <div className="container">
          <h2 className="heading-1" style={{ textAlign: 'center', marginBottom: '80px' }}>
            Dessert Services
          </h2>
          
          <div className="grid-two-column">
            {mockData.dessertServices.map((service, index) => (
              <Card key={service.id} className="hover-lift" style={{
                border: '1px solid var(--border-light)',
                borderRadius: '0px',
                background: 'var(--bg-primary)',
                padding: '40px 32px',
                textAlign: 'center'
              }}>
                <CardHeader>
                  <div style={{
                    width: '100px',
                    height: '100px',
                    background: index % 2 === 0 ? 'var(--color-gold)' : 'var(--color-fence-green)',
                    borderRadius: '0px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 32px auto'
                  }}>
                    <Cake size={40} color="white" />
                  </div>
                  <CardTitle className="heading-2">{service.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="body-large" style={{ marginBottom: '32px' }}>
                    {service.description}
                  </CardDescription>
                  <ul style={{ textAlign: 'left', listStyle: 'none', padding: 0 }}>
                    {service.features.map((feature, idx) => (
                      <li key={idx} className="body-regular" style={{ 
                        marginBottom: '12px',
                        color: 'var(--text-secondary)',
                        paddingLeft: '24px',
                        position: 'relative'
                      }}>
                        <span style={{
                          position: 'absolute',
                          left: 0,
                          top: '50%',
                          transform: 'translateY(-50%)',
                          width: '8px',
                          height: '8px',
                          background: 'var(--color-gold)',
                          borderRadius: '50%'
                        }}></span>
                        {feature}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Signature Menu Showcase */}
      <section className="section-padding">
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: '80px' }}>
            <h2 className="hero-medium" style={{ marginBottom: '24px' }}>Our Signature Tasting Experience</h2>
            <p className="body-large" style={{ color: 'var(--text-secondary)', maxWidth: '700px', margin: '0 auto' }}>
              Discover the artistry of our Caribbean, French, and American fusion cuisine through this carefully curated 
              3-course journey that showcases our culinary expertise and creativity.
            </p>
          </div>
          
          <div style={{ 
            display: 'flex', 
            justifyContent: 'center',
            marginBottom: '60px'
          }}>
            <img
              src="https://customer-assets.emergentagent.com/job_f440d90d-9a4a-40ff-939c-0a7c682d16a9/artifacts/2ftv77dc_IMG_1132.jpeg"
              alt="Decadent Palete - Teaser Menu featuring exquisite seafood and gourmet selections"
              style={{
                maxWidth: '100%',
                height: 'auto',
                maxHeight: '800px',
                objectFit: 'contain',
                borderRadius: '0px',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)'
              }}
            />
          </div>
          
          <div style={{
            textAlign: 'center',
            background: 'var(--color-lavender)',
            padding: '60px 40px',
            borderRadius: '0px'
          }}>
            <h3 className="heading-1" style={{ marginBottom: '24px' }}>Experience Our Tasting Menu</h3>
            <p className="body-large" style={{ 
              marginBottom: '40px', 
              color: 'var(--text-secondary)',
              maxWidth: '600px',
              margin: '0 auto 40px auto'
            }}>
              Available for private events, intimate dinners, and special occasions. Each dish is meticulously crafted 
              to tell the story of our unique culinary heritage and Indian influence.
            </p>
            
            <div style={{ display: 'flex', gap: '24px', justifyContent: 'center', flexWrap: 'wrap' }}>
              <Button className="btn-gold">
                Book Tasting Experience
              </Button>
              <Button 
                className="btn-primary"
              >
                Request Custom Menu
              </Button>
            </div>
            
            <div style={{ 
              marginTop: '40px', 
              padding: '24px',
              background: 'rgba(255, 255, 255, 0.5)',
              borderRadius: '0px'
            }}>
              <p className="body-small" style={{ 
                color: 'var(--text-secondary)',
                fontStyle: 'italic',
                margin: 0
              }}>
                *Menu items may vary based on seasonal availability and dietary requirements. 
                All dishes can be customized to accommodate allergies and preferences.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Indian Fusion Menu Section */}
      <section style={{ 
        background: 'linear-gradient(135deg, #000000 0%, #1a1a1a 100%)',
        padding: '80px 0',
        position: 'relative',
        overflow: 'hidden'
      }}>
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: `
            radial-gradient(circle at 25% 25%, rgba(212, 175, 55, 0.1) 0%, transparent 50%),
            radial-gradient(circle at 75% 75%, rgba(212, 175, 55, 0.05) 0%, transparent 50%)
          `
        }} />
        
        <div className="container" style={{ position: 'relative', zIndex: 2 }}>
          <div style={{ textAlign: 'center', maxWidth: '800px', margin: '0 auto 60px auto' }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginBottom: '24px'
            }}>
              <ChefHat style={{ color: '#d4af37', marginRight: '16px' }} size={48} />
              <h2 style={{
                fontSize: 'clamp(2rem, 4vw, 3rem)',
                fontWeight: '300',
                color: '#ffffff',
                margin: 0,
                fontFamily: "'Playfair Display', serif"
              }}>
                Caribbean Soul Cuisine Menu
              </h2>
            </div>
            
            <p style={{
              fontSize: '1.1rem',
              color: 'rgba(255, 255, 255, 0.8)',
              marginBottom: '32px',
              lineHeight: '1.7'
            }}>
              Experience the harmonious blend of Caribbean warmth, French sophistication, and Indian aromatic spices
            </p>
            
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              gap: '32px',
              marginBottom: '40px',
              flexWrap: 'wrap'
            }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ 
                  fontSize: '1.8rem', 
                  fontWeight: '600', 
                  color: '#d4af37',
                  fontFamily: "'Playfair Display', serif"
                }}>
                  Caribbean
                </div>
                <div style={{ fontSize: '0.9rem', color: 'rgba(255, 255, 255, 0.7)' }}>
                  Soul & Warmth
                </div>
              </div>
              
              <div style={{ textAlign: 'center' }}>
                <div style={{ 
                  fontSize: '1.8rem', 
                  fontWeight: '600', 
                  color: '#d4af37',
                  fontFamily: "'Playfair Display', serif"
                }}>
                  French
                </div>
                <div style={{ fontSize: '0.9rem', color: 'rgba(255, 255, 255, 0.7)' }}>
                  Technique & Elegance
                </div>
              </div>
              
              <div style={{ textAlign: 'center' }}>
                <div style={{ 
                  fontSize: '1.8rem', 
                  fontWeight: '600', 
                  color: '#d4af37',
                  fontFamily: "'Playfair Display', serif"
                }}>
                  Indian
                </div>
                <div style={{ fontSize: '0.9rem', color: 'rgba(255, 255, 255, 0.7)' }}>
                  Spice & Aromatics
                </div>
              </div>
            </div>
          </div>

          {/* Menu Categories Navigation */}
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            marginBottom: '60px',
            gap: '20px',
            flexWrap: 'wrap'
          }}>
            {menuCategories.map((category) => (
              <button
                key={category.key}
                onClick={() => setSelectedMenuCategory(category.key)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '16px 24px',
                  background: selectedMenuCategory === category.key 
                    ? 'linear-gradient(135deg, #d4af37 0%, #b8941f 100%)'
                    : 'transparent',
                  color: selectedMenuCategory === category.key ? '#000000' : '#ffffff',
                  border: selectedMenuCategory === category.key 
                    ? 'none' 
                    : '1px solid rgba(212, 175, 55, 0.3)',
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  if (selectedMenuCategory !== category.key) {
                    e.target.style.borderColor = 'rgba(212, 175, 55, 0.6)';
                    e.target.style.background = 'rgba(212, 175, 55, 0.1)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (selectedMenuCategory !== category.key) {
                    e.target.style.borderColor = 'rgba(212, 175, 55, 0.3)';
                    e.target.style.background = 'transparent';
                  }
                }}
              >
                {category.icon}
                {category.label}
              </button>
            ))}
          </div>

          {/* Menu Items */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
            gap: '32px',
            maxWidth: '1200px',
            margin: '0 auto'
          }}>
            {mockData.indianFusionMenu[selectedMenuCategory]?.map((item) => (
              <div
                key={item.id}
                style={{
                  background: '#000000',
                  border: '1px solid rgba(212, 175, 55, 0.2)',
                  borderRadius: '12px',
                  padding: '32px',
                  transition: 'all 0.3s ease',
                  position: 'relative',
                  overflow: 'hidden'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-4px)';
                  e.currentTarget.style.borderColor = 'rgba(212, 175, 55, 0.5)';
                  e.currentTarget.style.boxShadow = '0 8px 24px rgba(212, 175, 55, 0.15)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.borderColor = 'rgba(212, 175, 55, 0.2)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                {/* Subtle background pattern */}
                <div style={{
                  position: 'absolute',
                  top: 0,
                  right: 0,
                  width: '100px',
                  height: '100px',
                  background: 'linear-gradient(135deg, rgba(212, 175, 55, 0.05) 0%, transparent 70%)',
                  borderRadius: '0 12px 0 100%'
                }} />
                
                <div style={{ position: 'relative', zIndex: 2 }}>
                  {/* Item Header */}
                  <div style={{ marginBottom: '16px' }}>
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      marginBottom: '8px'
                    }}>
                      <h3 style={{
                        fontSize: '1.3rem',
                        fontWeight: '600',
                        color: '#ffffff',
                        margin: 0,
                        fontFamily: "'Playfair Display', serif"
                      }}>
                        {item.name}
                      </h3>
                      <div style={{
                        fontSize: '1.2rem',
                        fontWeight: '600',
                        color: '#d4af37'
                      }}>
                        Contact for Quote
                      </div>
                    </div>
                    
                    {/* Dietary and Spice Info */}
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      marginBottom: '12px'
                    }}>
                      {/* Spice Level */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span style={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '0.9rem' }}>
                          Heat:
                        </span>
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                          {getSpiceIcon(item.spiceLevel)}
                        </div>
                      </div>
                      
                      {/* Dietary Icon */}
                      {getDietaryIcon(item.dietaryInfo) && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                          {getDietaryIcon(item.dietaryInfo)}
                          <span style={{ 
                            color: 'rgba(255, 255, 255, 0.7)', 
                            fontSize: '0.85rem' 
                          }}>
                            {item.dietaryInfo.includes('Vegetarian') ? 'Vegetarian' : 
                             item.dietaryInfo.includes('Vegan') ? 'Vegan' : 'Premium'}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Description */}
                  <p style={{
                    color: 'rgba(255, 255, 255, 0.8)',
                    lineHeight: '1.6',
                    margin: '0 0 16px 0',
                    fontSize: '0.95rem'
                  }}>
                    {item.description}
                  </p>

                  {/* Ingredients/Features */}
                  <div style={{
                    borderTop: '1px solid rgba(212, 175, 55, 0.2)',
                    paddingTop: '16px',
                    marginTop: '16px'
                  }}>
                    <div style={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: '8px'
                    }}>
                      {item.ingredients?.slice(0, 4).map((ingredient, idx) => (
                        <span
                          key={idx}
                          style={{
                            background: 'rgba(212, 175, 55, 0.1)',
                            color: '#d4af37',
                            padding: '4px 8px',
                            borderRadius: '4px',
                            fontSize: '0.8rem',
                            border: '1px solid rgba(212, 175, 55, 0.3)'
                          }}
                        >
                          {ingredient}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Menu Footer Note */}
      <section style={{ background: '#1a1a1a', padding: '40px 0' }}>
        <div className="container">
          <div style={{ textAlign: 'center' }}>
            <p style={{
              color: 'rgba(255, 255, 255, 0.7)',
              fontStyle: 'italic',
              fontSize: '0.9rem',
              margin: 0
            }}>
              Our Caribbean Soul Cuisine menu showcases seasonal ingredients and can be customized for dietary preferences and allergies. 
              Each dish is crafted to order with the finest ingredients and authentic spice blends.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};